/**
 * Checkers Game Logic (American Checkers / Russian Checkers)
 * Handles game state, move validation, captures, and win detection
 * Supports both American and Russian variants
 */

// Constants
const BOARD_SIZE = 8;
const EMPTY = 0;
const BLACK_PIECE = 1;
const BLACK_KING = 2;
const RED_PIECE = 3;
const RED_KING = 4;

/**
 * Create initial checkers board
 * @returns {Array} 8x8 board with pieces in starting positions
 */
export const createInitialBoard = () => {
  const board = Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE).fill(EMPTY));

  // Place black pieces on rows 0-2 (dark squares only)
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      if ((row + col) % 2 === 1) { // Dark squares only
        board[row][col] = BLACK_PIECE;
      }
    }
  }

  // Place red pieces on rows 5-7 (dark squares only)
  for (let row = 5; row < BOARD_SIZE; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      if ((row + col) % 2 === 1) { // Dark squares only
        board[row][col] = RED_PIECE;
      }
    }
  }

  return board;
};

/**
 * Create initial game state
 */
export const createInitialGameState = (hostId, guestId, variant = 'american') => {
  // Randomly assign colors
  const hostIsBlack = Math.random() < 0.5;

  return {
    board: createInitialBoard(),
    currentTurn: 'black', // Black always goes first
    hostColor: hostIsBlack ? 'black' : 'red',
    guestColor: hostIsBlack ? 'red' : 'black',
    selectedPiece: null,
    mustJump: false, // Mandatory capture rule
    isGameOver: false,
    winner: null,
    moveHistory: [],
    capturedPieces: { black: 0, red: 0 },
    blackPiecesRemaining: 12,
    redPiecesRemaining: 12,
    blackKingsCount: 0,
    redKingsCount: 0,
    variant: variant, // Store variant in game state
  };
};

/**
 * Get piece type from board value
 */
export const getPieceType = (piece) => {
  if (piece === BLACK_PIECE || piece === BLACK_KING) return 'black';
  if (piece === RED_PIECE || piece === RED_KING) return 'red';
  return null;
};

/**
 * Check if piece is a king
 */
export const isKing = (piece) => {
  return piece === BLACK_KING || piece === RED_KING;
};

/**
 * Get all possible moves for a piece (including jumps)
 * Supports both American and Russian variants
 */
export const getPossibleMoves = (board, row, col, checkJumpsOnly = false, variant = 'american') => {
  const piece = board[row][col];
  if (piece === EMPTY) return [];

  const pieceColor = getPieceType(piece);
  const isKingPiece = isKing(piece);
  const moves = [];
  const jumps = [];

  // For Russian Checkers, kings can move multiple squares
  if (variant === 'russian' && isKingPiece) {
    return getRussianKingMoves(board, row, col, pieceColor, checkJumpsOnly);
  }

  // Directions: diagonal movements
  const directions = [];

  if (variant === 'russian') {
    // Russian: Regular pieces can capture in all directions
    directions.push([1, -1], [1, 1], [-1, -1], [-1, 1]);
  } else {
    // American: Regular pieces move forward only (kings can move any direction)
    if (pieceColor === 'black' || isKingPiece) {
      directions.push([1, -1], [1, 1]); // Forward for black (or any direction for king)
    }

    if (pieceColor === 'red' || isKingPiece) {
      directions.push([-1, -1], [-1, 1]); // Forward for red (or backward for king)
    }
  }

  // Check each direction
  for (const [dRow, dCol] of directions) {
    const newRow = row + dRow;
    const newCol = col + dCol;

    // For non-kings in Russian, only allow forward movement (not backward)
    // But allow captures in all directions
    const isForwardMove = (pieceColor === 'black' && dRow > 0) || (pieceColor === 'red' && dRow < 0);

    // Check simple move (only forward for non-kings)
    if (!checkJumpsOnly &&
        newRow >= 0 && newRow < BOARD_SIZE &&
        newCol >= 0 && newCol < BOARD_SIZE &&
        board[newRow][newCol] === EMPTY) {
      // Russian: non-kings can only move forward, not backward
      if (variant === 'russian' && !isKingPiece && !isForwardMove) {
        // Skip backward moves for regular pieces
      } else {
        moves.push({ row: newRow, col: newCol, isJump: false });
      }
    }

    // Check jump (all directions allowed for captures in Russian)
    const jumpRow = row + dRow * 2;
    const jumpCol = col + dCol * 2;

    if (newRow >= 0 && newRow < BOARD_SIZE &&
        newCol >= 0 && newCol < BOARD_SIZE &&
        jumpRow >= 0 && jumpRow < BOARD_SIZE &&
        jumpCol >= 0 && jumpCol < BOARD_SIZE) {

      const targetPiece = board[newRow][newCol];
      const landingSquare = board[jumpRow][jumpCol];

      // Can jump if there's an opponent piece and landing square is empty
      if (targetPiece !== EMPTY &&
          getPieceType(targetPiece) !== pieceColor &&
          landingSquare === EMPTY) {
        jumps.push({
          row: jumpRow,
          col: jumpCol,
          isJump: true,
          captureRow: newRow,
          captureCol: newCol
        });
      }
    }
  }

  // If there are jumps available, return only jumps (mandatory capture rule)
  if (jumps.length > 0) {
    return jumps;
  }

  return moves;
};

/**
 * Get moves for Russian Checkers flying kings
 */
const getRussianKingMoves = (board, row, col, pieceColor, checkJumpsOnly) => {
  const moves = [];
  const jumps = [];
  const directions = [[1, -1], [1, 1], [-1, -1], [-1, 1]];

  for (const [dRow, dCol] of directions) {
    let distance = 1;
    let foundOpponent = false;
    let opponentRow = -1;
    let opponentCol = -1;

    while (true) {
      const newRow = row + dRow * distance;
      const newCol = col + dCol * distance;

      // Check if out of bounds
      if (newRow < 0 || newRow >= BOARD_SIZE || newCol < 0 || newCol >= BOARD_SIZE) {
        break;
      }

      const targetSquare = board[newRow][newCol];

      if (!foundOpponent) {
        // Haven't found an opponent yet
        if (targetSquare === EMPTY) {
          // Can move here (if not checkJumpsOnly)
          if (!checkJumpsOnly) {
            moves.push({ row: newRow, col: newCol, isJump: false });
          }
          distance++;
        } else if (getPieceType(targetSquare) !== pieceColor) {
          // Found an opponent piece
          foundOpponent = true;
          opponentRow = newRow;
          opponentCol = newCol;
          distance++;
        } else {
          // Hit our own piece, stop
          break;
        }
      } else {
        // Already found an opponent, looking for landing squares
        if (targetSquare === EMPTY) {
          // Can jump here
          jumps.push({
            row: newRow,
            col: newCol,
            isJump: true,
            captureRow: opponentRow,
            captureCol: opponentCol
          });
          distance++;
        } else {
          // Hit another piece, stop
          break;
        }
      }
    }
  }

  // If there are jumps available, return only jumps (mandatory capture rule)
  if (jumps.length > 0) {
    return jumps;
  }

  return moves;
};

/**
 * Check if any piece of given color has a jump available
 */
export const hasJumpAvailable = (board, color, variant = 'american') => {
  for (let row = 0; row < BOARD_SIZE; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      const piece = board[row][col];
      if (piece !== EMPTY && getPieceType(piece) === color) {
        const moves = getPossibleMoves(board, row, col, true, variant);
        if (moves.some(m => m.isJump)) {
          return true;
        }
      }
    }
  }
  return false;
};

/**
 * Get all valid moves for the current turn (respecting mandatory jump rule)
 */
export const getAllValidMoves = (board, color, variant = 'american') => {
  const allMoves = [];
  const allJumps = [];

  for (let row = 0; row < BOARD_SIZE; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      const piece = board[row][col];
      if (piece !== EMPTY && getPieceType(piece) === color) {
        const moves = getPossibleMoves(board, row, col, false, variant);

        moves.forEach(move => {
          if (move.isJump) {
            allJumps.push({ fromRow: row, fromCol: col, ...move });
          } else {
            allMoves.push({ fromRow: row, fromCol: col, ...move });
          }
        });
      }
    }
  }

  // If any jumps available, return only jumps (mandatory capture)
  if (allJumps.length > 0) {
    return allJumps;
  }

  return allMoves;
};

/**
 * Validate if a move is legal
 */
export const isValidMove = (gameState, fromRow, fromCol, toRow, toCol, variant = 'american') => {
  const { board, currentTurn } = gameState;
  const piece = board[fromRow][fromCol];

  // Check if there's a piece at source
  if (piece === EMPTY) return false;

  // Check if piece belongs to current player
  if (getPieceType(piece) !== currentTurn) return false;

  // Get all possible moves for this piece
  const possibleMoves = getPossibleMoves(board, fromRow, fromCol, false, variant);

  // Check if any jump is available for current player
  const jumpAvailable = hasJumpAvailable(board, currentTurn, variant);

  // If jumps are available, only allow jump moves
  if (jumpAvailable) {
    return possibleMoves.some(m =>
      m.row === toRow && m.col === toCol && m.isJump
    );
  }

  // Otherwise, check if move is in possible moves
  return possibleMoves.some(m => m.row === toRow && m.col === toCol);
};

/**
 * Make a move and update game state
 */
export const makeMove = (gameState, fromRow, fromCol, toRow, toCol, variant = 'american') => {
  const newBoard = gameState.board.map(row => [...row]);
  const piece = newBoard[fromRow][fromCol];
  const pieceColor = getPieceType(piece);

  // Move piece
  newBoard[toRow][toCol] = piece;
  newBoard[fromRow][fromCol] = EMPTY;

  let capturedPiece = false;
  let newCapturedPieces = { ...gameState.capturedPieces };
  let blackRemaining = gameState.blackPiecesRemaining;
  let redRemaining = gameState.redPiecesRemaining;
  let captureRow = -1;
  let captureCol = -1;

  // Check if this was a jump (capture)
  // For American: jump is always 2 squares away
  // For Russian kings: could be any distance
  const rowDiff = Math.abs(toRow - fromRow);
  const colDiff = Math.abs(toCol - fromCol);

  if (rowDiff >= 2 && rowDiff === colDiff) {
    // This is a jump - find and remove the captured piece
    const rowDir = toRow > fromRow ? 1 : -1;
    const colDir = toCol > fromCol ? 1 : -1;

    // Search along the diagonal for the opponent piece
    let searchRow = fromRow + rowDir;
    let searchCol = fromCol + colDir;

    while (searchRow !== toRow || searchCol !== toCol) {
      const searchPiece = newBoard[searchRow][searchCol];
      if (searchPiece !== EMPTY && getPieceType(searchPiece) !== pieceColor) {
        // Found the captured piece
        capturedPiece = true;
        captureRow = searchRow;
        captureCol = searchCol;
        const capturedPieceType = getPieceType(searchPiece);

        newBoard[searchRow][searchCol] = EMPTY;

        // Update captured pieces count
        if (capturedPieceType === 'black') {
          newCapturedPieces.black += 1;
          blackRemaining -= 1;
        } else {
          newCapturedPieces.red += 1;
          redRemaining -= 1;
        }
        break;
      }
      searchRow += rowDir;
      searchCol += colDir;
    }
  }

  // Check for king promotion
  let blackKings = gameState.blackKingsCount;
  let redKings = gameState.redKingsCount;
  let promotedToKing = false;

  if (pieceColor === 'black' && toRow === 7 && piece === BLACK_PIECE) {
    newBoard[toRow][toCol] = BLACK_KING;
    blackKings += 1;
    promotedToKing = true;
  } else if (pieceColor === 'red' && toRow === 0 && piece === RED_PIECE) {
    newBoard[toRow][toCol] = RED_KING;
    redKings += 1;
    promotedToKing = true;
  }

  // Check for multi-jump
  let nextTurn = pieceColor === 'black' ? 'red' : 'black';
  let continueJumping = false;

  if (capturedPiece) {
    // American: If promoted, turn ends
    // Russian: If promoted, king continues capturing if possible
    if (variant === 'american' && promotedToKing) {
      // Turn ends on promotion in American Checkers
      continueJumping = false;
    } else {
      // Check if more jumps available from new position
      const additionalJumps = getPossibleMoves(newBoard, toRow, toCol, true, variant);
      if (additionalJumps.some(m => m.isJump)) {
        // Must continue jumping with same piece
        continueJumping = true;
        nextTurn = pieceColor; // Keep same turn
      }
    }
  }

  const newGameState = {
    ...gameState,
    board: newBoard,
    currentTurn: nextTurn,
    capturedPieces: newCapturedPieces,
    blackPiecesRemaining: blackRemaining,
    redPiecesRemaining: redRemaining,
    blackKingsCount: blackKings,
    redKingsCount: redKings,
    moveHistory: [
      ...(Array.isArray(gameState.moveHistory) ? gameState.moveHistory : []),
      { from: { row: fromRow, col: fromCol }, to: { row: toRow, col: toCol }, captured: capturedPiece }
    ],
    selectedPiece: continueJumping ? { row: toRow, col: toCol } : null,
    variant: variant, // Keep variant in state
  };

  // Check win condition
  const gameStatus = checkGameEnd(newGameState, variant);
  if (gameStatus.isGameOver) {
    newGameState.isGameOver = true;
    newGameState.winner = gameStatus.winner;
  }

  return newGameState;
};

/**
 * Check if game has ended
 */
export const checkGameEnd = (gameState, variant = 'american') => {
  const { board, currentTurn, blackPiecesRemaining, redPiecesRemaining } = gameState;

  // Check if either player has no pieces left
  if (blackPiecesRemaining === 0) {
    return { isGameOver: true, winner: 'red' };
  }

  if (redPiecesRemaining === 0) {
    return { isGameOver: true, winner: 'black' };
  }

  // Check if current player has no legal moves (blocked)
  const validMoves = getAllValidMoves(board, currentTurn, variant);
  if (validMoves.length === 0) {
    const winner = currentTurn === 'black' ? 'red' : 'black';
    return { isGameOver: true, winner };
  }

  return { isGameOver: false, winner: null };
};

/**
 * Export constants for use in components
 */
export const PIECE_CONSTANTS = {
  EMPTY,
  BLACK_PIECE,
  BLACK_KING,
  RED_PIECE,
  RED_KING,
};
